import Phaser from 'phaser';
import { GAME_FONT_FAMILY } from '@shared/constants';
import { ICON_ATLASES, createCloseButton, type IconAtlasConfig } from '../../sprites/IconAtlases';

/**
 * Visor de íconos in-game. Herramienta dev para mapear nombres a coordenadas
 * en los atlases registrados. Toggle via Ctrl+I (wireado en GameScene).
 *
 * Funcionalidades:
 * - Selector de atlas (un botón por entry en `ICON_ATLASES`).
 * - Grid escalado del atlas (cellSize del viewer configurable).
 * - Borde verde sobre frames que ya tienen nombre mapeado.
 * - Hover: tooltip con (row, col, frame) y nombre si está mapeado.
 * - Click: selecciona la celda; muestra abajo el snippet listo para copiar.
 * - Scroll vertical con la rueda para atlases grandes (atlas 3 tiene ~2200
 *   frames).
 */

const PANEL_WIDTH = 540;
const PANEL_HEIGHT = 460;
const HEADER_HEIGHT = 28;
const TOOLBAR_HEIGHT = 32;
const FOOTER_HEIGHT = 78;
const GRID_PADDING = 8;
const VIEWER_CELL_OPTIONS = [16, 24, 32, 48] as const;
const DEFAULT_VIEWER_CELL = 32;

const COLOR_BG = 0x1a1a22;
const COLOR_HEADER = 0x2c2c38;
const COLOR_BORDER = 0x444455;
const COLOR_BUTTON = 0x3a3a48;
const COLOR_BUTTON_ACTIVE = 0x6688aa;
const COLOR_MAPPED = 0x44ff66;
const COLOR_SELECTED = 0xffee44;
const COLOR_HOVER = 0xffffff;

function withGameFont<T extends Phaser.Types.GameObjects.Text.TextStyle>(style?: T): T {
  return { fontFamily: GAME_FONT_FAMILY, resolution: 2, ...style } as T;
}

function buildFrameToNameMap(atlas: IconAtlasConfig): Map<number, string> {
  const m = new Map<number, string>();
  for (const [name, frame] of Object.entries(atlas.icons)) {
    m.set(frame, name);
  }
  return m;
}

interface SelectedCell {
  atlasIndex: number;
  row: number;
  col: number;
  frame: number;
}

type ViewerPanelOptions = {
  initialX: number;
  initialY: number;
};

export class IconAtlasViewerPanel {
  private readonly scene: Phaser.Scene;
  private container!: Phaser.GameObjects.Container;
  private bg!: Phaser.GameObjects.Rectangle;
  private header!: Phaser.GameObjects.Rectangle;
  private titleText!: Phaser.GameObjects.Text;
  private closeBtn!: Phaser.GameObjects.Sprite | Phaser.GameObjects.Text;
  private atlasButtons: Phaser.GameObjects.Text[] = [];
  private cellSizeText!: Phaser.GameObjects.Text;
  private gridContainer!: Phaser.GameObjects.Container;
  private selectionOverlay!: Phaser.GameObjects.Rectangle;
  private hoverOverlay!: Phaser.GameObjects.Rectangle;
  private footerSelText!: Phaser.GameObjects.Text;
  private footerNameText!: Phaser.GameObjects.Text;
  private footerSnippetText!: Phaser.GameObjects.Text;
  private tooltipContainer!: Phaser.GameObjects.Container;
  private tooltipBg!: Phaser.GameObjects.Rectangle;
  private tooltipText!: Phaser.GameObjects.Text;

  private isOpen = false;
  private panelX = 0;
  private panelY = 0;
  private isDragging = false;
  private dragOffsetX = 0;
  private dragOffsetY = 0;

  private currentAtlasIndex = 0;
  private viewerCellSize: number = DEFAULT_VIEWER_CELL;
  private scrollY = 0;
  private selected: SelectedCell | null = null;
  private hoveredFrame: number | null = null;

  constructor(scene: Phaser.Scene, options: ViewerPanelOptions) {
    this.scene = scene;
    this.panelX = options.initialX;
    this.panelY = options.initialY;
    this.build();
    this.setVisible(false);
  }

  toggle(): void {
    this.isOpen ? this.close() : this.open();
  }
  open(): void {
    this.isOpen = true;
    this.setVisible(true);
    this.refreshGrid();
  }
  close(): void {
    this.isOpen = false;
    this.setVisible(false);
    this.tooltipContainer?.setVisible(false);
  }
  isOpenPanel(): boolean {
    return this.isOpen;
  }

  /** True si el pointer está dentro del rectángulo del panel (origin 0 en panelX/panelY). */
  isPointerOver(pointer: Phaser.Input.Pointer): boolean {
    if (!this.isOpen) return false;
    return (
      pointer.x >= this.panelX &&
      pointer.x <= this.panelX + PANEL_WIDTH &&
      pointer.y >= this.panelY &&
      pointer.y <= this.panelY + PANEL_HEIGHT
    );
  }

  private setVisible(v: boolean): void {
    this.container.setVisible(v);
  }

  private build(): void {
    const s = this.scene;
    this.container = s.add.container(this.panelX, this.panelY).setDepth(990).setScrollFactor(0);

    this.bg = s.add
      .rectangle(0, 0, PANEL_WIDTH, PANEL_HEIGHT, COLOR_BG, 0.95)
      .setOrigin(0)
      .setStrokeStyle(1, COLOR_BORDER)
      // Fondo interactivo: absorbe hover/click de objetos del mundo debajo
      // (Phaser topOnly) para que el panel no deje pasar clicks ni cambie el cursor.
      .setInteractive();
    this.container.add(this.bg);

    this.header = s.add
      .rectangle(0, 0, PANEL_WIDTH, HEADER_HEIGHT, COLOR_HEADER, 1)
      .setOrigin(0)
      .setInteractive({ useHandCursor: true, draggable: false });
    this.container.add(this.header);

    this.titleText = s.add
      .text(8, 6, 'Icon Atlas Viewer', withGameFont({ fontSize: '13px', color: '#ffffff', fontStyle: 'bold' }))
      .setOrigin(0);
    this.container.add(this.titleText);

    // Drag desde el header.
    this.header.on('pointerdown', (p: Phaser.Input.Pointer) => {
      this.isDragging = true;
      this.dragOffsetX = p.x - this.panelX;
      this.dragOffsetY = p.y - this.panelY;
    });
    s.input.on('pointerup', () => {
      this.isDragging = false;
    });
    s.input.on('pointermove', (p: Phaser.Input.Pointer) => {
      if (!this.isDragging) return;
      this.panelX = p.x - this.dragOffsetX;
      this.panelY = p.y - this.dragOffsetY;
      this.container.setPosition(this.panelX, this.panelY);
    });

    // Close button (esquina superior derecha).
    const cb = createCloseButton(s, PANEL_WIDTH - 6, 6, 16);
    if (cb) {
      cb.setOrigin(1, 0);
      cb.on('pointerup', () => this.close());
      this.container.add(cb);
      this.closeBtn = cb;
    } else {
      const fallback = s.add
        .text(PANEL_WIDTH - 8, 4, '✕', withGameFont({ fontSize: '14px', color: '#ffffff' }))
        .setOrigin(1, 0)
        .setInteractive({ useHandCursor: true });
      fallback.on('pointerup', () => this.close());
      this.container.add(fallback);
      this.closeBtn = fallback;
    }

    // Toolbar: botones de atlas + cell size selector.
    const toolbarY = HEADER_HEIGHT + 6;
    let bx = 8;
    ICON_ATLASES.forEach((atlas, i) => {
      const label = `${atlas.key} (${Object.keys(atlas.icons).length}/${atlas.cols * atlas.rows})`;
      const btn = s.add
        .text(bx, toolbarY, label, withGameFont({
          fontSize: '11px', color: '#dddddd', backgroundColor: '#3a3a48',
          padding: { x: 6, y: 3 },
        }))
        .setOrigin(0)
        .setInteractive({ useHandCursor: true });
      btn.on('pointerup', () => this.selectAtlas(i));
      this.container.add(btn);
      this.atlasButtons.push(btn);
      bx += btn.width + 4;
    });

    this.cellSizeText = s.add
      .text(PANEL_WIDTH - 8, toolbarY + 2, `Cell: ${this.viewerCellSize}px [click]`,
        withGameFont({ fontSize: '11px', color: '#aaccff' }))
      .setOrigin(1, 0)
      .setInteractive({ useHandCursor: true });
    this.cellSizeText.on('pointerup', () => this.cycleCellSize());
    this.container.add(this.cellSizeText);

    // Grid container. Sin mask (dev tool — overflow tolerable).
    const gridY = HEADER_HEIGHT + TOOLBAR_HEIGHT;
    this.gridContainer = s.add.container(GRID_PADDING, gridY + GRID_PADDING);
    this.container.add(this.gridContainer);

    // Overlays de selección y hover (encima del grid).
    this.hoverOverlay = s.add
      .rectangle(0, 0, this.viewerCellSize, this.viewerCellSize, 0xffffff, 0)
      .setOrigin(0)
      .setStrokeStyle(1, COLOR_HOVER)
      .setVisible(false);
    this.gridContainer.add(this.hoverOverlay);

    this.selectionOverlay = s.add
      .rectangle(0, 0, this.viewerCellSize, this.viewerCellSize, 0xffffff, 0)
      .setOrigin(0)
      .setStrokeStyle(2, COLOR_SELECTED)
      .setVisible(false);
    this.gridContainer.add(this.selectionOverlay);

    // Footer con info de selección.
    const footerY = PANEL_HEIGHT - FOOTER_HEIGHT + 6;
    this.footerSelText = s.add
      .text(8, footerY, 'Selected: —', withGameFont({ fontSize: '11px', color: '#cccccc' }))
      .setOrigin(0);
    this.footerNameText = s.add
      .text(8, footerY + 18, 'Name: —', withGameFont({ fontSize: '11px', color: '#cccccc' }))
      .setOrigin(0);
    this.footerSnippetText = s.add
      .text(8, footerY + 38, 'Snippet: —', withGameFont({
        fontSize: '11px', color: '#ffee99', fontStyle: 'bold',
      }))
      .setOrigin(0);
    this.container.add([this.footerSelText, this.footerNameText, this.footerSnippetText]);

    // Tooltip flotante (world coords, NO hijo del container — sigue al mouse).
    // Se ancla arriba a la derecha del cursor.
    this.tooltipContainer = s.add.container(0, 0).setDepth(995).setScrollFactor(0).setVisible(false);
    this.tooltipBg = s.add
      .rectangle(0, 0, 10, 10, 0x000000, 0.85)
      .setOrigin(0)
      .setStrokeStyle(1, 0x666688);
    this.tooltipText = s.add
      .text(6, 4, '', withGameFont({ fontSize: '11px', color: '#ffffff' }))
      .setOrigin(0);
    this.tooltipContainer.add([this.tooltipBg, this.tooltipText]);

    // Scroll con la rueda del mouse (sobre el panel).
    s.input.on('wheel', (
      _p: Phaser.Input.Pointer,
      _go: unknown,
      _dx: number,
      dy: number,
    ) => {
      if (!this.isOpen) return;
      // Sólo scrollea si el cursor está dentro del panel.
      const mx = s.input.activePointer.x;
      const my = s.input.activePointer.y;
      if (mx < this.panelX || mx > this.panelX + PANEL_WIDTH) return;
      if (my < this.panelY + HEADER_HEIGHT + TOOLBAR_HEIGHT || my > this.panelY + PANEL_HEIGHT - FOOTER_HEIGHT) return;
      this.scrollY = Math.min(0, this.scrollY - dy);
      this.gridContainer.y = HEADER_HEIGHT + TOOLBAR_HEIGHT + GRID_PADDING + this.scrollY;
    });

    this.selectAtlas(0);
  }

  private cycleCellSize(): void {
    const idx = VIEWER_CELL_OPTIONS.indexOf(this.viewerCellSize as (typeof VIEWER_CELL_OPTIONS)[number]);
    this.viewerCellSize = VIEWER_CELL_OPTIONS[(idx + 1) % VIEWER_CELL_OPTIONS.length];
    this.cellSizeText.setText(`Cell: ${this.viewerCellSize}px [click]`);
    this.refreshGrid();
  }

  private selectAtlas(i: number): void {
    this.currentAtlasIndex = i;
    this.scrollY = 0;
    this.atlasButtons.forEach((btn, j) => {
      btn.setStyle({ backgroundColor: j === i ? '#6688aa' : '#3a3a48' });
    });
    this.refreshGrid();
  }

  private refreshGrid(): void {
    const s = this.scene;
    const atlas = ICON_ATLASES[this.currentAtlasIndex];
    if (!atlas) return;

    // Limpiar grid (manteniendo overlays).
    const keep = new Set([this.hoverOverlay, this.selectionOverlay]);
    this.gridContainer.list
      .filter((c) => !keep.has(c as Phaser.GameObjects.Rectangle))
      .forEach((c) => c.destroy());

    if (!s.textures.exists(atlas.key)) {
      const warning = s.add
        .text(8, 8, `Texture '${atlas.key}' no cargada todavía.`,
          withGameFont({ fontSize: '11px', color: '#ff8888' }))
        .setOrigin(0);
      this.gridContainer.add(warning);
      return;
    }

    const cell = this.viewerCellSize;
    const cols = atlas.cols;
    const rows = atlas.rows;
    const frameToName = buildFrameToNameMap(atlas);

    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const frame = r * cols + c;
        const x = c * cell;
        const y = r * cell;
        const sprite = s.add
          .sprite(x, y, atlas.key, frame)
          .setOrigin(0)
          .setDisplaySize(cell, cell)
          .setInteractive({ useHandCursor: true });

        // Borde verde si el frame está mapeado.
        if (frameToName.has(frame)) {
          const mark = s.add
            .rectangle(x, y, cell, cell, 0xffffff, 0)
            .setOrigin(0)
            .setStrokeStyle(1, COLOR_MAPPED);
          this.gridContainer.add(mark);
        }

        sprite.on('pointerover', () => {
          this.hoveredFrame = frame;
          this.hoverOverlay.setPosition(x, y).setSize(cell, cell).setVisible(true);
          this.showHoverTooltip(r + 1, c + 1, frame, frameToName.get(frame));
        });
        sprite.on('pointermove', (p: Phaser.Input.Pointer) => {
          if (this.hoveredFrame === frame) {
            this.positionTooltipAtPointer(p);
          }
        });
        sprite.on('pointerout', () => {
          if (this.hoveredFrame === frame) {
            this.hoveredFrame = null;
            this.hoverOverlay.setVisible(false);
            this.tooltipContainer.setVisible(false);
          }
        });
        sprite.on('pointerup', () => {
          this.selectCell(r + 1, c + 1, frame, frameToName.get(frame));
        });

        this.gridContainer.add(sprite);
      }
    }

    // Reposicionar overlays al frente.
    this.gridContainer.bringToTop(this.hoverOverlay);
    this.gridContainer.bringToTop(this.selectionOverlay);
    this.selectionOverlay.setVisible(false);
  }

  /** Muestra el tooltip flotante con coords + nombre del frame hovered. */
  private showHoverTooltip(row: number, col: number, frame: number, name?: string): void {
    const tag = name ?? '(unmapped)';
    this.tooltipText.setText(`${tag}\n(row ${row}, col ${col}) frame ${frame}`);
    // Ajustar bg al tamaño del texto.
    const padX = 6;
    const padY = 4;
    this.tooltipBg.setSize(this.tooltipText.width + padX * 2, this.tooltipText.height + padY * 2);
    this.tooltipText.setPosition(padX, padY);
    this.tooltipContainer.setVisible(true);
    this.positionTooltipAtPointer(this.scene.input.activePointer);
  }

  /** Ancla el tooltip arriba-a-la-derecha del cursor. */
  private positionTooltipAtPointer(p: Phaser.Input.Pointer): void {
    const offset = 8;
    const w = this.tooltipBg.width;
    const h = this.tooltipBg.height;
    // Arriba a la derecha del cursor: pegado a la derecha (origen 0), por encima (- h - offset).
    let x = p.x + offset;
    let y = p.y - h - offset;
    // Clamp para que no se salga de la pantalla.
    const { width: sw, height: sh } = this.scene.scale;
    if (x + w > sw) x = p.x - w - offset;
    if (y < 0) y = p.y + offset;
    if (y + h > sh) y = sh - h;
    this.tooltipContainer.setPosition(x, y);
  }

  private selectCell(row: number, col: number, frame: number, name?: string): void {
    const atlas = ICON_ATLASES[this.currentAtlasIndex];
    if (!atlas) return;
    this.selected = { atlasIndex: this.currentAtlasIndex, row, col, frame };
    const cell = this.viewerCellSize;
    this.selectionOverlay
      .setPosition((col - 1) * cell, (row - 1) * cell)
      .setSize(cell, cell)
      .setVisible(true);
    const tag = name ?? '<nuevo>';
    this.footerSelText.setText(
      `Selected: ${atlas.key} — row ${row}, col ${col} → frame ${frame}`,
    );
    this.footerNameText.setText(`Name: ${tag}`);
    this.footerSnippetText.setText(
      `Snippet: ${name ?? '<name>'}: frameOf(${row}, ${col}, ${atlas.cols}),`,
    );
  }
}
